import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, CircularProgress, InputAdornment,
    useMediaQuery, Paper, Chip, IconButton
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import InfoIcon from "@mui/icons-material/Info";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import CloseIcon from '@mui/icons-material/Close';

import api from "../../services/api";
import { getApiErrorMessage, normalizePlan } from "../../services/apiUtils";
import { useSnackbar } from "../../contexts/SnackbarContext";

const digits = (v = "") => String(v).replace(/\D/g, "");

const CARD_RULES = {
    amex: { max: 15, groups: [4, 6, 5] },
    diners: { max: 14, groups: [4, 6, 4] },
    default: { max: 16, groups: [4, 4, 4, 4] },
};

const detectBrand = (num) => {
    const n = digits(num);

    if (/^3[47]/.test(n)) return "amex";
    if (/^4/.test(n)) return "visa";
    if (/^(5[1-5]|2[2-7])/.test(n)) return "mastercard";
    if (/^(606282|3841)/.test(n)) return "hipercard";
    if (/^(4011|4312|4389|4514|4576|5041|5066|5067|5090|6277|6362|6363)/.test(n)) return "elo";
    if (/^(36|30[0-5]|38|39)/.test(n)) return "diners";
    if (/^(6011|65|64[4-9]|622)/.test(n)) return "discover";
    if (/^35/.test(n)) return "jcb";

    return null;
};

const formatCardNumber = (v) => {
    const rawDigits = digits(v);

    const b = detectBrand(rawDigits);
    const rule = CARD_RULES[b] || CARD_RULES.default;

    const d = rawDigits.slice(0, rule.max);

    const parts = [];
    let i = 0;
    for (const size of rule.groups) {
        if (i >= d.length) break;
        parts.push(d.slice(i, i + size));
        i += size;
    }
    if (i < d.length) parts.push(d.slice(i));

    return parts.join(" ");
};

const formatExpiry = (v) => {
    const d = digits(v).slice(0, 4);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}/${d.slice(2)}`;
};
const onlyLetters = (v) =>
    String(v).replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, "").replace(/\s{2,}/g, " ");

export default function PaymentMethodDialog({ open, onClose, userId }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState(null);

    const [edit, setEdit] = useState(false);
    const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
    const { showSnackbar } = useSnackbar();

    const brand = useMemo(() => detectBrand(card.number), [card.number]);

    const loadInfo = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const res = await api.get("/billing/plan");
            const plan = normalizePlan(res.data);
            setInfo({
                ...plan,
                hasCard: Boolean(plan?.creditCardToken || plan?.creditCardLast4),
                brand: plan?.creditCardBrand,
                last4: plan?.creditCardLast4,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        setEdit(false);
        setCard({ number: "", name: "", expiry: "", cvv: "" });
        loadInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleClose = () => {
        if (loading) return;
        onClose?.();
    };

    const isFormValid = useMemo(() => {
        const num = digits(card.number);
        const exp = digits(card.expiry);
        const cvv = digits(card.cvv);

        const b = detectBrand(num);
        const rule = CARD_RULES[b] || CARD_RULES.default;

        const cvvNeeded = b === "amex" ? 4 : 3;

        return (
            num.length === rule.max &&
            (card.name || "").trim().length >= 3 &&
            exp.length === 4 &&
            cvv.length === cvvNeeded
        );
    }, [card]);

    const handleUpdate = async () => {
        if (!isFormValid) return;

        const num = digits(card.number);
        const exp = digits(card.expiry);
        const cvv = digits(card.cvv);

        const expiryMonth = exp.slice(0, 2);
        const expiryYear = `20${exp.slice(2)}`;

        setLoading(true);
        try {
            await api.patch("/billing/subscription/credit-card", {
                creditCard: {
                    holderName: card.name.trim(),
                    number: num,
                    expiryMonth,
                    expiryYear,
                    ccv: cvv.slice(0, 4),
                },
            });

            setEdit(false);
            await loadInfo();
            showSnackbar("Cartão atualizado com sucesso.", "success");
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Não foi possível atualizar o cartão."), "error");
        } finally {
            setLoading(false);
        }
    };

    const CardSummary = (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 3,
                borderColor: alpha(theme.palette.divider, 0.8),
                bgcolor: alpha(theme.palette.primary.main, 0.03)
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <CreditCardRoundedIcon color="action" />
                    <Box>
                        <Typography sx={{ fontWeight: 900 }}>Método de pagamento</Typography>
                        {info?.hasCard ? (
                            <Typography sx={{ color: "text.secondary" }}>
                                {String(info?.brand || "").toUpperCase()} •••• {info?.last4}
                            </Typography>
                        ) : (
                            <Typography sx={{ color: "text.secondary" }}>
                                Nenhum cartão cadastrado
                            </Typography>
                        )}
                    </Box>
                </Box>

                {info?.hasCard ? (
                    <Chip
                        icon={<CheckCircleRoundedIcon />}
                        label="Ativo"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 900 }}
                    />
                ) : (
                    <Chip
                        label="Pendente"
                        size="small"
                        sx={{ fontWeight: 900 }}
                    />
                )}
            </Box>

            <Typography
                variant="caption"
                sx={{ display: "block", mt: 1.2, color: "text.secondary" }}
            >
                <InfoIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                O cartão é tokenizado. A Docen não armazena número e CVV.
            </Typography>

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                    variant="contained"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => setEdit(true)}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    Alterar cartão
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    Fechar
                </Button>
            </Box>
        </Paper>
    );

    const EditForm = (
        <Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                {["visa", "mastercard", "elo", "diners", "discover", "amex", "hipercard", "jcb"].map((b) => (
                    <Box
                        key={b}
                        component="img"
                        alt={b}
                        src={`/cards/${b}.svg`}
                        sx={{ height: 22, opacity: brand && brand !== b ? 0.35 : 1 }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ))}
            </Box>

            <Typography variant="caption" sx={{ display: "block", mb: 2, color: "text.secondary" }}>
                <InfoIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
                Você pode atualizar o cartão a qualquer momento. Isso vale para as próximas cobranças.
            </Typography>

            <TextField
                label="Número do cartão"
                fullWidth
                value={card.number}
                onChange={(e) => setCard(v => ({ ...v, number: formatCardNumber(e.target.value) }))}
                inputMode="numeric"
                sx={{ mb: 2 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Typography variant="caption" sx={{ fontWeight: 900 }}>
                                {brand ? brand.toUpperCase() : ""}
                            </Typography>
                        </InputAdornment>
                    )
                }}
            />

            <TextField
                label="Nome do titular"
                fullWidth
                value={card.name}
                onChange={(e) => setCard(v => ({ ...v, name: onlyLetters(e.target.value) }))}
                sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                    label="Validade (MM/AA)"
                    fullWidth
                    value={card.expiry}
                    onChange={(e) => setCard(v => ({ ...v, expiry: formatExpiry(e.target.value) }))}
                    inputMode="numeric"
                />
                <TextField
                    label="CVV"
                    fullWidth
                    value={card.cvv}
                    onChange={(e) => {
                        const b = detectBrand(card.number);
                        const max = b === "amex" ? 4 : 3;
                        setCard(v => ({ ...v, cvv: digits(e.target.value).slice(0, max) }));
                    }}
                    inputMode="numeric"
                />
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            fullScreen={isMobile}
            PaperProps={{
                sx: { p: 0, borderRadius: isMobile ? 0 : 3 }
            }}
        >
            <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
                Pagamento
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 10 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ pb: isMobile ? 10 : 3 }}>
                {loading && !info ? (
                    <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {!edit ? CardSummary : EditForm}
                    </>
                )}
            </DialogContent>

            {/* CTA fixo quando editando (melhor no mobile) */}
            <DialogActions
                sx={{
                    p: 2,
                    display: edit ? "flex" : "none",
                    position: isMobile ? "fixed" : "static",
                    bottom: isMobile ? 0 : "auto",
                    left: isMobile ? 0 : "auto",
                    right: isMobile ? 0 : "auto",
                    bgcolor: isMobile ? "background.paper" : "transparent",
                    borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
                    zIndex: 10
                }}
            >
                <Button
                    onClick={() => setEdit(false)}
                    disabled={loading}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    Voltar
                </Button>

                <Button
                    onClick={handleUpdate}
                    disabled={loading || !isFormValid}
                    variant="contained"
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Salvar cartão"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}